import type { QueriesOptions } from "@tanstack/react-query";
import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import parseISO from "date-fns/parseISO";

import { getDataFromResponse } from "~/api/axiosUtils";
import {
	getTimeTracking,
	getUsersDayStatesByMonth,
	getUsersTimeTrackings,
	indexProjectsTimeTrackings,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingApiDispatchers.ts";
import type { GetUsersTimeTrackingsFilterType } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { getStartAndEndOfMonthFormatted } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";

export const TIME_TRACKING_BASE_QUERY_KEY = "timeTracking";

export const TIME_TRACKINGS_BY_USER_QUERY_KEY = (userId: string,
	filter?: GetUsersTimeTrackingsFilterType) => [TIME_TRACKING_BASE_QUERY_KEY, { userId }, filter];
/** The key of the tracking page's index for one month. `month` is `YYYY-MM`. */
export const TIME_TRACKINGS_BY_USER_AND_MONTH_QUERY_KEY = (userId: string, month: string) =>
	TIME_TRACKINGS_BY_USER_QUERY_KEY(userId, getStartAndEndOfMonthFormatted(parseISO(`${month}-01`)));
export const TIME_TRACKING_QUERY_KEY = (timeTrackingId: string) => [TIME_TRACKING_BASE_QUERY_KEY, { timeTrackingId }];

export const PROJECTS_TIME_TRACKINGS_QUERY_KEY = (projectId: string) => [TIME_TRACKING_BASE_QUERY_KEY, { projectId }];
export const useUsersTimeTrackings = ({ userId, filter, enabled = true, keepPreviousFilter = false }: {
	userId: string,
	filter?: GetUsersTimeTrackingsFilterType
	enabled?: boolean
	keepPreviousFilter?: boolean
}) => useQuery({
	queryKey: TIME_TRACKINGS_BY_USER_QUERY_KEY(userId, filter),
	queryFn: () => getUsersTimeTrackings(userId, filter).then(getDataFromResponse),
	enabled,
	placeholderData: keepPreviousFilter ? keepPreviousData : undefined,
});

export const useProjectsTimeTrackings = (projectId: string) => useQuery({
	queryKey: PROJECTS_TIME_TRACKINGS_QUERY_KEY(projectId),
	queryFn: () => indexProjectsTimeTrackings(projectId).then(getDataFromResponse),
	enabled: !!projectId,
});

/** `month` is `YYYY-MM`. The key shares the base key, so each change of a tracking refreshes the day states too. */
export const dayStateQueries = {
	byMonth: (userId: string, month: string) => queryOptions({
		queryKey: [TIME_TRACKING_BASE_QUERY_KEY, "dayStates", { userId, month }],
		queryFn: () => getUsersDayStatesByMonth({ userId, month }).then(getDataFromResponse),
		enabled: !!userId,
	}),
};

export const useTimeTracking = ({ userId, timeTrackingId, options }: {
	userId: string, timeTrackingId: string, options?: QueriesOptions<any>
}) => useQuery({
	queryKey: TIME_TRACKING_QUERY_KEY(timeTrackingId),
	queryFn: () => getTimeTracking(userId, timeTrackingId).then(getDataFromResponse),
	...options,
});